// Shared frame for org-scoped settings pages (/{org}/settings/…, IA §2.1 group 11): the page
// heading, a lead line and the permission gate. A direct link to a page the role can't open renders
// permission-denied (the nav already hides it; the server still enforces every route).
// Layout per D-038: a settings section bar across every settings page, an 18 px bold title, and
// white cards with a tinted border.
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx, ErrorState, Icon, StatePanel } from "@ady/ui";
import { useRouterState } from "@tanstack/react-router";
import { AppLink } from "../shell/AppLink";
import { ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { useShell } from "../shell/context";
import { can, hrefFor, iconFor, itemForPathname, visibleNav } from "../shell/nav";

/** The settings section bar: every settings page the role can open, the current one tinted. */
export function SettingsSections() {
  const { membership, orgSlug, storeSlug } = useShell();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const items = visibleNav(membership).find((g) => g.id === "settings")?.items ?? [];
  const isCurrent = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
  // Only the closest match is current (Courier accounts, not also Delivery and couriers).
  const currentId = itemForPathname(pathname, orgSlug, storeSlug)?.id;
  // Other areas (inventory, POS, reviews…) reuse this frame; the bar belongs to settings pages only.
  if (items.length < 2 || !items.some((i) => isCurrent(hrefFor(i, orgSlug, storeSlug))))
    return null;
  return (
    <nav
      aria-label={t("shell.settingsSections")}
      className="ady-scroll-quiet flex max-w-full gap-1 self-start overflow-x-auto rounded-sm border border-card bg-surface p-1"
    >
      {items.map((item) => {
        const href = hrefFor(item, orgSlug, storeSlug);
        const current = item.id === currentId;
        return (
          <AppLink
            key={item.id}
            href={href}
            aria-current={current ? "page" : undefined}
            className={cx(
              "flex min-h-control flex-none items-center gap-2 rounded-sm px-3 text-sm font-medium whitespace-nowrap no-underline",
              current ? "ady-bg-brand-tint" : "text-fg-tertiary hover:bg-hover hover:text-fg",
            )}
          >
            <Icon name={iconFor(item)} size={16} gradient={current} />
            <span className={current ? "ady-text-brand" : undefined}>{t(item.label)}</span>
          </AppLink>
        );
      })}
    </nav>
  );
}
import { TID } from "../testids";
import { PageHeader } from "../shell/PageHeader";

export function SettingsPage({
  title,
  lead,
  anyOf,
  testId,
  canonical,
  actions,
  children,
}: {
  title: string;
  lead?: ReactNode;
  /** Permissions that open the page (any of them). */
  anyOf: string[];
  testId: string;
  canonical: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const { membership } = useShell();
  if (!can(membership, anyOf)) {
    return (
      <StatePanel
        kind="permission-denied"
        size="page"
        headingLevel={2}
        title={t("states.permissionDeniedTitle")}
        body={t("states.permissionDeniedBody", { who: t("states.permissionWhoOwner") })}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid={testId}
      data-state="ready"
      data-canonical={canonical}
    >
      <PageHeader title={title} lead={lead} actions={actions} />
      {children}
    </div>
  );
}

/** A titled card section inside a settings page. */
export function SettingsSection({
  title,
  aside,
  children,
  id,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  id: string;
}) {
  return (
    <section
      className="flex flex-col gap-4 rounded-xs border border-card bg-surface p-4 sm:p-5"
      aria-labelledby={`${id}-h`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`${id}-h`} className="text-md font-bold">
          {title}
        </h2>
        {aside ? <span className="text-sm text-fg-secondary">{aside}</span> : null}
      </header>
      {children}
    </section>
  );
}

/** The error state for a failed load, with Retry and the request ID. */
export function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ErrorState
      size="inline"
      headingLevel={3}
      body={errorMessage(error)}
      requestId={error instanceof ApiError ? error.requestId : undefined}
      onRetry={onRetry}
      data-testid={TID.errorState}
    />
  );
}
